import { Component, FC, ReactNode, useEffect, useRef, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third-party
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
	CheckOutlined,
	CloseOutlined,
	CopyOutlined,
	DownOutlined,
	ExclamationCircleOutlined
} from '@ant-design/icons';

// project imports
import IconButton from '@/components/@extended/IconButton';
import { PopupTransition } from '@/components/@extended/Transitions';
import {
	categoryLabel,
	CATEGORY_TOAST_COLORS,
	CATEGORY_TOAST_VARIANT
} from './category-ui';
import { CodeBlock } from './code-block';
import { useErrorContext } from './context';
import { ErrorCategory, IErrorLogEntry } from './types';

// ==============================|| ERRORS - SESSION LOG DIALOG (CTZ-2758) ||============================== //
// Jurnalul erorilor din sesiunea curentă: fiecare intrare cu moment, categorie+sursă,
// mesajul prietenos (RO) și detalii tehnice expandabile (BE: endpoint, status, request
// id, payload brut; FE: mesaj exact, stack). Copiere per intrare + copiere integrală —
// detaliile se pot atașa direct unei sesizări. Deschis din toast (focalizat pe intrarea
// respectivă) sau programatic (openDialog()).

const Detail: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
	<Stack
		direction="row"
		sx={{ gap: 2, justifyContent: 'space-between', alignItems: 'baseline' }}
	>
		<Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
			{value}
		</Typography>
	</Stack>
);

const formatTs = (iso: string): string => {
	try {
		return format(parseISO(iso), 'dd MMM yyyy, HH:mm:ss', { locale: ro });
	} catch {
		return iso;
	}
};

const formatTime = (iso: string): string => {
	try {
		return format(parseISO(iso), 'HH:mm:ss', { locale: ro });
	} catch {
		return iso;
	}
};

// Chip-ul de categorie — aceleași culori ca toastul: variantele semantice ale temei,
// cu override explicit acolo unde tema nu are variantă proprie (galbenul runtime FE).
const CategoryChip: FC<{ category: ErrorCategory }> = ({ category }) => {
	const override = CATEGORY_TOAST_COLORS[category];
	if (override) {
		return (
			<Chip
				size="small"
				label={categoryLabel(category)}
				sx={{ bgcolor: override.main, color: override.contrastText, fontWeight: 600 }}
			/>
		);
	}
	return (
		<Chip
			size="small"
			variant="light"
			color={CATEGORY_TOAST_VARIANT[category]}
			label={categoryLabel(category)}
		/>
	);
};

const entryAsText = (entry: IErrorLogEntry): string => JSON.stringify(entry, null, 2);

const ErrorDetailsDialogContent: FC = () => {
	// i18n cu fallback hardcodat (defaultValue): textele sunt editabile din Gestionare
	// texte (grupul „errors"), dar dialogul funcționează și fără traducerile încărcate.
	const { t } = useTranslation();
	const { entries, dialogOpen, focusedEntryId, closeDialog, clearLog } =
		useErrorContext();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const [copiedAll, setCopiedAll] = useState(false);
	const focusedRef = useRef<HTMLDivElement | null>(null);

	// la deschiderea din toast: intrarea respectivă e expandată și derulată în vedere
	useEffect(() => {
		if (!dialogOpen) return;
		setExpandedId(focusedEntryId);
		if (focusedEntryId) {
			requestAnimationFrame(() =>
				focusedRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
			);
		}
	}, [dialogOpen, focusedEntryId]);

	const copyText = async (text: string): Promise<boolean> => {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			return false; // clipboard indisponibil
		}
	};

	const handleCopyEntry = async (entry: IErrorLogEntry): Promise<void> => {
		if (await copyText(entryAsText(entry))) {
			setCopiedId(entry.id);
			setTimeout(
				() => setCopiedId((current) => (current === entry.id ? null : current)),
				1500
			);
		}
	};

	const handleCopyAll = async (): Promise<void> => {
		if (await copyText(JSON.stringify(entries, null, 2))) {
			setCopiedAll(true);
			setTimeout(() => setCopiedAll(false), 1500);
		}
	};

	return (
		<Dialog
			open={dialogOpen}
			onClose={closeDialog}
			maxWidth="md"
			fullWidth
			slots={{ transition: PopupTransition }}
			aria-labelledby="error-log-dialog-title"
		>
			<DialogTitle id="error-log-dialog-title">
				<Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
					<Box
						component="span"
						sx={{ color: 'error.main', display: 'inline-flex', fontSize: '1.25rem' }}
					>
						<ExclamationCircleOutlined />
					</Box>
					<Typography variant="h4" sx={{ flex: 1 }}>
						{t('errors.dialogTitle', { defaultValue: 'Erori în sesiunea curentă' })}
					</Typography>
					{entries.length > 0 && (
						<Chip size="small" variant="light" color="error" label={entries.length} />
					)}
					<IconButton
						color="secondary"
						onClick={closeDialog}
						aria-label={t('errors.close', { defaultValue: 'Închide' })}
					>
						<CloseOutlined />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ p: 2 }}>
				{entries.length === 0 ? (
					<Stack sx={{ py: 6, alignItems: 'center', gap: 0.5 }}>
						<Typography variant="subtitle1">
							{t('errors.dialogEmpty', {
								defaultValue: 'Nicio eroare înregistrată în sesiunea curentă.'
							})}
						</Typography>
						<Typography variant="caption" sx={{ color: 'text.secondary' }}>
							{t('errors.dialogEmptyHint', {
								defaultValue:
									'Erorile aplicației și ale serverului apar aici pe măsură ce se întâmplă.'
							})}
						</Typography>
					</Stack>
				) : (
					<Stack sx={{ gap: 1 }}>
						{entries.map((entry) => {
							const { error } = entry;
							const isBE = error.source === 'BE';
							const tone = CATEGORY_TOAST_VARIANT[error.category];
							const expanded = expandedId === entry.id;
							const focused = focusedEntryId === entry.id;

							return (
								<Box
									key={entry.id}
									ref={focused ? focusedRef : undefined}
									sx={{
										border: '1px solid',
										borderColor: focused ? `${tone}.main` : 'divider',
										borderRadius: 1,
										overflow: 'hidden'
									}}
								>
									{/* rândul-rezumat — click pentru detalii */}
									<Stack
										direction="row"
										onClick={() => setExpandedId(expanded ? null : entry.id)}
										role="button"
										tabIndex={0}
										onKeyDown={(e) =>
											e.key === 'Enter' && setExpandedId(expanded ? null : entry.id)
										}
										sx={{
											gap: 1,
											alignItems: 'center',
											px: 1.5,
											py: 1,
											cursor: 'pointer',
											'&:hover': { bgcolor: 'action.hover' }
										}}
									>
										<Typography
											variant="caption"
											sx={{
												color: 'text.secondary',
												fontVariantNumeric: 'tabular-nums',
												flexShrink: 0
											}}
										>
											{formatTime(entry.lastAt)}
										</Typography>
										<CategoryChip category={error.category} />
										{isBE && error.status != null && (
											<Chip
												size="small"
												variant="outlined"
												color={tone}
												label={error.status}
											/>
										)}
										<Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
											{error.friendlyMessage}
										</Typography>
										{entry.count > 1 && (
											<Chip
												size="small"
												variant="light"
												color={tone}
												label={`×${entry.count}`}
											/>
										)}
										<IconButton
											size="small"
											color="secondary"
											aria-label={t('errors.copyEntry', {
												defaultValue: 'Copiază detaliile erorii'
											})}
											onClick={(e) => {
												e.stopPropagation();
												handleCopyEntry(entry);
											}}
										>
											{copiedId === entry.id ? <CheckOutlined /> : <CopyOutlined />}
										</IconButton>
										<Box
											component="span"
											sx={{
												display: 'inline-flex',
												color: 'text.secondary',
												fontSize: '0.7rem',
												transition: 'transform .2s',
												transform: expanded ? 'rotate(180deg)' : 'none'
											}}
										>
											<DownOutlined />
										</Box>
									</Stack>

									{/* detaliile tehnice expandabile */}
									<Collapse in={expanded} unmountOnExit>
										<Stack sx={{ gap: 1.5, px: 1.5, pb: 1.5, pt: 0.5 }}>
											<Alert severity="error" color={tone}>
												<AlertTitle>
													{error.title ?? (isBE ? 'Eroare BE' : 'Eroare FE')}
												</AlertTitle>
												{error.message}
											</Alert>
											<Stack sx={{ gap: 1 }}>
												<Detail
													label={t('errors.detailSource', { defaultValue: 'Sursă' })}
													value={
														isBE
															? t('errors.detailSourceBe', {
																	defaultValue: 'Backend (BE)'
																})
															: t('errors.detailSourceFe', {
																	defaultValue: 'Frontend (FE)'
																})
													}
												/>
												{error.code && (
													<Detail
														label={t('errors.detailCode', { defaultValue: 'Cod' })}
														value={error.code}
													/>
												)}
												{isBE && error.status != null && (
													<Detail
														label={t('errors.detailStatus', { defaultValue: 'Status' })}
														value={`${error.status} ${error.statusText ?? ''}`.trim()}
													/>
												)}
												{isBE && !error.status && error.statusText && (
													<Detail
														label={t('errors.detailStatusText', {
															defaultValue: 'Detaliu'
														})}
														value={error.statusText}
													/>
												)}
												{isBE && error.method && (
													<Detail
														label={t('errors.detailRequest', { defaultValue: 'Cerere' })}
														value={`${error.method} ${error.url ?? ''}`.trim()}
													/>
												)}
												{error.requestId && (
													<Detail
														label={t('errors.detailRequestId', {
															defaultValue: 'Request ID'
														})}
														value={error.requestId}
													/>
												)}
												<Detail
													label={t('errors.detailMoment', { defaultValue: 'Moment' })}
													value={formatTs(error.timestamp)}
												/>
												{entry.count > 1 && (
													<Detail
														label={t('errors.detailRepeats', {
															defaultValue: 'Repetări'
														})}
														value={t('errors.detailRepeatsValue', {
															defaultValue: 'de {{times}} ori (prima: {{first}})',
															times: entry.count,
															first: formatTs(entry.firstAt)
														})}
													/>
												)}
											</Stack>
											{isBE && error.payload !== undefined && (
												<Stack sx={{ gap: 0.5 }}>
													<Typography variant="caption" sx={{ color: 'text.secondary' }}>
														{t('errors.detailPayload', {
															defaultValue: 'Răspuns server (brut)'
														})}
													</Typography>
													<CodeBlock>
														{typeof error.payload === 'string'
															? error.payload
															: JSON.stringify(error.payload, null, 2)}
													</CodeBlock>
												</Stack>
											)}
											{!isBE && error.stack && (
												<Stack sx={{ gap: 0.5 }}>
													<Typography variant="caption" sx={{ color: 'text.secondary' }}>
														{t('errors.detailStack', { defaultValue: 'Stack trace' })}
													</Typography>
													<CodeBlock>{error.stack}</CodeBlock>
												</Stack>
											)}
										</Stack>
									</Collapse>
								</Box>
							);
						})}
					</Stack>
				)}
			</DialogContent>

			<DialogActions>
				{entries.length > 0 && (
					<>
						<Button color="secondary" onClick={clearLog}>
							{t('errors.clearLog', { defaultValue: 'Golește jurnalul' })}
						</Button>
						<Box sx={{ flex: 1 }} />
						<Button
							color="secondary"
							startIcon={copiedAll ? <CheckOutlined /> : <CopyOutlined />}
							onClick={handleCopyAll}
						>
							{copiedAll
								? t('errors.copied', { defaultValue: 'Copiat' })
								: t('errors.copyAll', { defaultValue: 'Copiază tot' })}
						</Button>
					</>
				)}
				<Button variant="contained" onClick={closeDialog}>
					{t('errors.close', { defaultValue: 'Închide' })}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ==============================|| ERRORS - DIALOG SELF-GUARD ||============================== //
// If the dialog itself crashes while rendering an error, render nothing instead of
// taking down the whole app; log only (no re-report, so no infinite loop).

class DialogGuard extends Component<{ children: ReactNode }, { failed: boolean }> {
	constructor(props: { children: ReactNode }) {
		super(props);
		this.state = { failed: false };
	}

	static getDerivedStateFromError(): { failed: boolean } {
		return { failed: true };
	}

	componentDidCatch(error: Error): void {
		console.error('ErrorDetailsDialog failed to render:', error);
	}

	render(): ReactNode {
		const { failed } = this.state;
		const { children } = this.props;
		return failed ? null : children;
	}
}

export const ErrorDetailsDialog: FC = () => {
	const { dialogOpen, focusedEntryId } = useErrorContext();
	// key by open state + focused entry so the guard resets on every (re)opening
	return (
		<DialogGuard key={dialogOpen ? (focusedEntryId ?? 'all') : 'closed'}>
			<ErrorDetailsDialogContent />
		</DialogGuard>
	);
};
